import React, { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import { PawPrint } from "lucide-react";
import { Link } from "react-router-dom";
import { placeUrl } from "../lib/placeUrl";

// Whole-U.S. view used only when there are no pins to show.
const US_CENTER = [39.5, -98.35];
const US_ZOOM = 4;

const isValidCoord = (p) =>
  p && Number.isFinite(p.lat) && Number.isFinite(p.lng) &&
  Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180;

const makeIcon = (active) =>
  L.divIcon({
    className: "paw-divicon",
    html: `<div class="paw-marker ${active ? "active" : ""}">${renderToStaticMarkup(
      <PawPrint size={18} strokeWidth={2.5} />
    )}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });

const FitBounds = ({ parks, fitToken, suppressFitRef }) => {
  const map = useMap();
  // Use a ref so the fit effect always sees latest parks without re-running on park changes.
  const parksRef = useRef(parks);
  parksRef.current = parks;
  useEffect(() => {
    const current = parksRef.current;
    if (!current?.length) return;
    const size = map.getSize();
    if (!size.x || !size.y) return;
    if (suppressFitRef) suppressFitRef.current = true;
    if (current.length === 1) {
      map.setView([current[0].lat, current[0].lng], 13);
      return;
    }
    const bounds = L.latLngBounds(current.map((p) => [p.lat, p.lng]));
    if (!bounds.isValid()) return;
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitToken, map]);
  return null;
};

const FlyToActive = ({ parks, activeId }) => {
  const map = useMap();
  useEffect(() => {
    if (!activeId) return;
    const p = parks.find((x) => x.id === activeId);
    if (p && isValidCoord(p)) map.flyTo([p.lat, p.lng], Math.max(map.getZoom(), 13), { duration: 0.6 });
  }, [activeId, parks, map]);
  return null;
};

// When the user taps "Use my current location" we both fly the camera to
// their coordinates and drop a distinct "You are here" marker. Updates on
// every new userLocation reference so re-tapping after a permission grant
// still re-centers.
const FlyToUserLocation = ({ userLocation, suppressFitRef }) => {
  const map = useMap();
  useEffect(() => {
    if (!userLocation || !isValidCoord(userLocation)) return;
    if (suppressFitRef) suppressFitRef.current = true;
    map.flyTo(
      [userLocation.lat, userLocation.lng],
      Math.max(map.getZoom(), 13),
      { duration: 0.6 }
    );
  }, [userLocation, map, suppressFitRef]);
  return null;
};

const userIcon = L.divIcon({
  className: "paw-divicon",
  html: `<div class="user-location-marker"></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const InvalidateOnResize = () => {
  const map = useMap();
  useEffect(() => {
    const refresh = () => map.invalidateSize();
    refresh();
    const t = setTimeout(refresh, 300);
    window.addEventListener("resize", refresh);
    window.addEventListener("orientationchange", refresh);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(refresh);
      ro.observe(map.getContainer());
    }
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", refresh);
      window.removeEventListener("orientationchange", refresh);
      ro?.disconnect();
    };
  }, [map]);
  return null;
};

// Emit map area on user-driven pan/zoom only. We deliberately listen to
// `dragend` + `zoomend` rather than the general `moveend` because
// `moveend` also fires when `invalidateSize()` runs (e.g. when the
// mobile URL bar hides/shows and the map container resizes mid-scroll).
// Listening to user-gesture events only prevents bogus auto-refetches
// while the user is just scrolling the results list.
const AreaEmitter = ({ onAreaChange, suppressFitRef }) => {
  const emit = (map) => {
    if (suppressFitRef?.current) {
      // Consume the suppression — next user drag will emit normally.
      suppressFitRef.current = false;
      return;
    }
    const c = map.getCenter();
    const b = map.getBounds();
    const radius = b ? c.distanceTo(b.getNorthEast()) : null;
    onAreaChange?.({ lat: c.lat, lng: c.lng, zoom: map.getZoom(), radius });
  };
  useMapEvents({
    dragend: (e) => emit(e.target),
    zoomend: (e) => emit(e.target),
  });
  return null;
};

const MapView = ({ parks = [], activeId, onMarkerClick, onAreaChange, fitToken = 0, userLocation = null }) => {
  // Skip pins for delivery-only listings — their lat/lng exists only
  // to satisfy the model + power the 2dsphere search index, not to
  // guide users to a driveable location. See models/parks.py::visit_type.
  const validParks = parks.filter((p) => isValidCoord(p) && p.visit_type !== "delivery_only");
  const center = validParks.length ? [validParks[0].lat, validParks[0].lng] : US_CENTER;
  const suppressFitRef = useRef(false);

  return (
    <MapContainer
      center={center}
      zoom={validParks.length ? 11 : US_ZOOM}
      className="w-full h-full"
      data-testid="map-view"
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <InvalidateOnResize />
      <FitBounds parks={validParks} fitToken={fitToken} suppressFitRef={suppressFitRef} />
      <FlyToActive parks={validParks} activeId={activeId} />
      <FlyToUserLocation userLocation={userLocation} suppressFitRef={suppressFitRef} />
      <AreaEmitter onAreaChange={onAreaChange} suppressFitRef={suppressFitRef} />
      {userLocation && isValidCoord(userLocation) && (
        <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
          <Popup>
            <div className="text-xs font-semibold text-brand-ink">You are here</div>
          </Popup>
        </Marker>
      )}
      {validParks.map((p) => (
        <Marker
          key={p.id}
          position={[p.lat, p.lng]}
          icon={makeIcon(p.id === activeId)}
          eventHandlers={{ click: () => onMarkerClick?.(p.id) }}
        >
          <Popup>
            <div className="min-w-[180px]">
              <div className="font-heading font-semibold text-[15px] text-brand-ink mb-1">{p.name}</div>
              <div className="text-xs text-brand-ink/60 mb-1">{p.city}, {p.state}</div>
              <div className="text-xs text-brand-ink mb-2">⭐ {p.rating?.toFixed(1)} · {p.active_checkins} dog{p.active_checkins === 1 ? "" : "s"} here</div>
              <Link
                to={placeUrl(p)}
                className="text-xs font-semibold text-brand-forest hover:text-brand-forest-dark"
              >
                View park →
              </Link>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

export default MapView;
